/**
 * Authentication Context
 *
 * Provides auth state and methods throughout the app.
 * Demo login goes through the real backend auth flow — the backend
 * returns is_demo: true for the demo user.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import { enableDemo, disableDemo, DEMO_SESSION_KEY } from '../services/demoMode';

const AuthContext = createContext(null);

// Minimal fallback for isolated UI development when the backend is unreachable.
// Only used when VITE_DEMO_FALLBACK=true AND the real login fails.
const DEMO_FALLBACK_USER = {
  id: 'demo-user-fallback',
  email: 'demo@adflowio.com',
  first_name: 'Demo',
  last_name: 'User',
  full_name: 'Demo User',
  role: 'owner',
  plan: 'enterprise',
  is_demo: true,
  email_verified: true,
  current_team_id: 'demo-team-fallback',
  team: {
    id: 'demo-team-fallback',
    name: 'Acme Ecommerce',
    plan: 'enterprise',
  },
  subscription: {
    plan_tier: 'enterprise',
    status: 'active',
  },
};

const DEMO_CREDENTIALS = {
  email: 'demo@adflowio.com',
  password: 'Demo1234!',
};

const isFallbackDemoEnabled = () => {
  // Explicit opt-in
  if (import.meta.env.VITE_DEMO_FALLBACK === 'true') return true;
  // Auto-enable in local dev when no API URL or pointing at localhost
  const apiUrl = import.meta.env.VITE_API_URL || '';
  return !apiUrl || apiUrl.includes('localhost');
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      // No-login demo session (per-tab). Survives refresh; store re-seeds in-memory.
      if (sessionStorage.getItem(DEMO_SESSION_KEY) === 'true') {
        enableDemo();
        setUser(DEMO_FALLBACK_USER);
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      // If we had a frontend-only fallback session, restore it
      if (localStorage.getItem('demo_fallback') === 'true' && isFallbackDemoEnabled()) {
        setUser(DEMO_FALLBACK_USER);
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      if (authService.isAuthenticated()) {
        try {
          const userData = await authService.getCurrentUser();
          setUser(userData);
          setIsAuthenticated(true);
        } catch {
          localStorage.removeItem('demo_fallback');
          setIsAuthenticated(false);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    disableDemo();
    setIsLoading(true);
    try {
      await authService.login(email, password);
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
      localStorage.removeItem('demo_fallback');
      return { success: true };
    } catch (error) {
      // Frontend-only fallback: only if env flag set AND demo credentials
      if (
        isFallbackDemoEnabled() &&
        email === DEMO_CREDENTIALS.email &&
        password === DEMO_CREDENTIALS.password
      ) {
        localStorage.setItem('demo_fallback', 'true');
        setUser(DEMO_FALLBACK_USER);
        setIsAuthenticated(true);
        return { success: true };
      }
      return { success: false, error: error.message || 'Login failed' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginDemo = useCallback(async () => {
    return login(DEMO_CREDENTIALS.email, DEMO_CREDENTIALS.password);
  }, [login]);

  const enterDemo = useCallback(() => {
    enableDemo();
    setUser(DEMO_FALLBACK_USER);
    setIsAuthenticated(true);
    setIsLoading(false);
  }, []);

  const register = useCallback(async (userData) => {
    disableDemo();
    setIsLoading(true);
    try {
      await authService.register(userData);
      const user = await authService.getCurrentUser();
      setUser(user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      localStorage.removeItem('demo_fallback');
      disableDemo();
      await authService.logout();
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated,
    isDemoAccount: user?.is_demo === true,
    login,
    loginDemo,
    enterDemo,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
